/**
 * WeaveIn.ai Platform - Emergency Campaign Wizard
 * Step wizard: Build audience -> Compose message -> Configure waves -> Review & Launch
 */

import React, { useState, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Users,
  MessageSquare,
  Layers,
  Rocket,
  ChevronRight,
  ChevronLeft,
  AlertTriangle,
  MapPin,
  Phone,
  Mail,
  Bot,
  ClipboardCheck,
  Plus,
  Trash2,
} from 'lucide-react';
import { emergencyAPI, AudienceFilters, AudiencePreview, WaveConfig } from '@/services/api/emergency';
import { useTabbedChrome } from '@/components/weavein';

// ============================================================================
// WIZARD STEPS
// ============================================================================

const STEPS = [
  { id: 'audience', label: 'Build Audience', icon: Users },
  { id: 'message', label: 'Compose Message', icon: MessageSquare },
  { id: 'waves', label: 'Configure Waves', icon: Layers },
  { id: 'review', label: 'Review & Launch', icon: Rocket },
];

// ============================================================================
// AUDIENCE STEP
// ============================================================================

const AudienceStep: React.FC<{
  filters: AudienceFilters;
  onChange: (f: AudienceFilters) => void;
  preview: AudiencePreview | null;
  isLoadingPreview: boolean;
  onPreviewAll: () => void;
}> = ({ filters, onChange, preview, isLoadingPreview, onPreviewAll }) => {
  const [zipInput, setZipInput] = useState('');

  const addZips = () => {
    const newZips = zipInput.split(/[,\s]+/).map(z => z.trim()).filter(z => /^\d{5}$/.test(z));
    if (newZips.length > 0) {
      onChange({ ...filters, zip_codes: [...(filters.zip_codes || []), ...newZips] });
      setZipInput('');
    }
  };

  return (
    <div className="space-y-4">
      {/* ZIP Codes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Codes</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={zipInput}
            onChange={e => setZipInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addZips()}
            placeholder="Enter ZIP codes (comma-separated)"
            className="flex-1 px-3 py-2 border rounded-md text-sm"
          />
          <button onClick={addZips} className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm">Add</button>
        </div>
        {(filters.zip_codes || []).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {filters.zip_codes!.map(z => (
              <span key={z} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                {z}
                <button onClick={() => onChange({ ...filters, zip_codes: filters.zip_codes!.filter(x => x !== z) })} className="hover:text-red-500">&times;</button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* States */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">States</label>
        <input
          type="text"
          value={(filters.states || []).join(', ')}
          onChange={e => onChange({ ...filters, states: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
          placeholder="e.g., TX, FL, LA"
          className="w-full px-3 py-2 border rounded-md text-sm"
        />
      </div>

      {/* Risk Levels */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Risk Levels</label>
        <div className="flex gap-2">
          {['critical', 'at_risk', 'watch', 'healthy'].map(level => (
            <label key={level} className="flex items-center gap-1.5 text-xs">
              <input
                type="checkbox"
                checked={(filters.risk_levels || []).includes(level)}
                onChange={e => {
                  const current = filters.risk_levels || [];
                  onChange({
                    ...filters,
                    risk_levels: e.target.checked
                      ? [...current, level]
                      : current.filter(l => l !== level),
                  });
                }}
                className="rounded"
              />
              {level.replace('_', ' ')}
            </label>
          ))}
        </div>
      </div>

      {/* Preview actions */}
      <div className="flex items-center gap-3 mt-2">
        <button
          onClick={onPreviewAll}
          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
        >
          Preview Audience
        </button>
        <span className="text-xs text-gray-500">
          {!preview ? 'Set filters above or click Preview to see all eligible members' : ''}
        </span>
      </div>

      {/* Preview */}
      {preview && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
          <h4 className="text-sm font-semibold mb-2">Audience Preview</h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-2xl font-bold text-blue-600">{preview.total_members}</div>
              <div className="text-xs text-gray-500">Total Members</div>
            </div>
            <div>
              <div className="text-2xl font-bold">{preview.zip_codes_covered}</div>
              <div className="text-xs text-gray-500">ZIP Codes</div>
            </div>
            <div>
              <div className="text-2xl font-bold">{preview.avg_engagement_score?.toFixed(0) || '—'}</div>
              <div className="text-xs text-gray-500">Avg Engagement</div>
            </div>
          </div>
          {Object.keys(preview.by_risk_level).length > 0 && (
            <div className="mt-3">
              <div className="text-xs font-medium text-gray-600 mb-1">Risk Distribution</div>
              <div className="flex gap-2">
                {Object.entries(preview.by_risk_level).map(([level, count]) => (
                  <span key={level} className="text-xs px-2 py-0.5 bg-white rounded border">
                    {level}: <b>{count}</b>
                  </span>
                ))}
              </div>
            </div>
          )}
          {Object.keys(preview.estimated_cost).length > 0 && (
            <div className="mt-2 text-xs text-gray-500">
              Est. cost: {Object.entries(preview.estimated_cost).map(([ch, cost]) =>
                `${ch}: $${cost.toFixed(2)}`
              ).join(' | ')}
            </div>
          )}
        </div>
      )}
      {isLoadingPreview && (
        <div className="text-sm text-gray-400 text-center py-4">Computing audience preview...</div>
      )}
    </div>
  );
};

// ============================================================================
// WAVE CONFIGURATOR
// ============================================================================

const WaveConfigurator: React.FC<{
  waves: WaveConfig[];
  onChange: (waves: WaveConfig[]) => void;
}> = ({ waves, onChange }) => {
  const channelOptions = [
    { value: 'sms', label: 'SMS Blast', icon: <Phone className="w-4 h-4" /> },
    { value: 'voice_ai', label: 'Voice AI Call', icon: <Bot className="w-4 h-4" /> },
    { value: 'email', label: 'Email', icon: <Mail className="w-4 h-4" /> },
    { value: 'case_manager_task', label: 'Case Manager Task', icon: <ClipboardCheck className="w-4 h-4" /> },
  ];

  const addWave = () => {
    onChange([...waves, {
      wave: waves.length + 1,
      channel: waves.length === 0 ? 'sms' : waves.length === 1 ? 'voice_ai' : 'case_manager_task',
      delay_after_previous: waves.length === 0 ? undefined : '4h',
      message_template: '',
      target: waves.length === 0 ? 'all' : 'non_responders',
    }]);
  };

  const updateWave = (index: number, updates: Partial<WaveConfig>) => {
    const newWaves = waves.map((w, i) => i === index ? { ...w, ...updates } : w);
    onChange(newWaves);
  };

  const removeWave = (index: number) => {
    onChange(waves.filter((_, i) => i !== index).map((w, i) => ({ ...w, wave: i + 1 })));
  };

  return (
    <div className="space-y-4">
      {waves.map((wave, i) => (
        <div key={i} className="border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold">Wave {wave.wave}</span>
            {waves.length > 1 && (
              <button onClick={() => removeWave(i)} className="text-gray-400 hover:text-red-500">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Channel</label>
              <select
                value={wave.channel}
                onChange={e => updateWave(i, { channel: e.target.value })}
                className="w-full px-2 py-1.5 border rounded text-sm"
              >
                {channelOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {i > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Delay After Previous</label>
                <select
                  value={wave.delay_after_previous || '4h'}
                  onChange={e => updateWave(i, { delay_after_previous: e.target.value })}
                  className="w-full px-2 py-1.5 border rounded text-sm"
                >
                  <option value="1h">1 hour</option>
                  <option value="2h">2 hours</option>
                  <option value="4h">4 hours</option>
                  <option value="8h">8 hours</option>
                  <option value="12h">12 hours</option>
                  <option value="1d">1 day</option>
                </select>
              </div>
            )}
          </div>

          {wave.channel !== 'case_manager_task' && (
            <div className="mt-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {wave.channel === 'voice_ai' ? 'Voice Script' : 'Message Template'}
              </label>
              <textarea
                value={wave.message_template}
                onChange={e => updateWave(i, { message_template: e.target.value })}
                placeholder={
                  wave.channel === 'voice_ai'
                    ? 'This is {org_name} checking on you. Press 1 if you are safe...'
                    : '{first_name}, this is an emergency notice. Reply SAFE if you are okay.'
                }
                rows={3}
                className="w-full px-3 py-2 border rounded-md text-sm"
              />
            </div>
          )}

          {wave.channel === 'voice_ai' && (
            <div className="mt-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Voice Preset</label>
              <select
                value={wave.voice_preset || 'friendly_female'}
                onChange={e => updateWave(i, { voice_preset: e.target.value })}
                className="w-full px-2 py-1.5 border rounded text-sm"
              >
                <option value="friendly_female">Friendly Female (en-US)</option>
                <option value="professional_female">Professional Female (en-US)</option>
                <option value="friendly_male">Friendly Male (en-US)</option>
                <option value="spanish_female">Spanish Female (es-US)</option>
                <option value="spanish_male">Spanish Male (es-US)</option>
              </select>
            </div>
          )}
        </div>
      ))}

      <button
        onClick={addWave}
        className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800"
      >
        <Plus className="w-4 h-4" />
        Add Wave
      </button>
    </div>
  );
};

// ============================================================================
// MAIN WIZARD
// ============================================================================

interface EmergencyCampaignWizardProps {
  incidentId: number;
  incidentName: string;
  onComplete: () => void;
  onCancel: () => void;
}

export const EmergencyCampaignWizard: React.FC<EmergencyCampaignWizardProps> = ({
  incidentId,
  incidentName,
  onComplete,
  onCancel,
}) => {
  const tabbed = useTabbedChrome();
  const [currentStep, setCurrentStep] = useState(0);
  const [filters, setFilters] = useState<AudienceFilters>({ consent_tier: 'emergency' });
  const [waves, setWaves] = useState<WaveConfig[]>([
    { wave: 1, channel: 'sms', message_template: `ALERT: ${incidentName} may affect your area. Reply SAFE if you are okay, HELP if you need assistance.`, target: 'all' },
    { wave: 2, channel: 'voice_ai', delay_after_previous: '4h', message_template: `This is a welfare check regarding ${incidentName}. Press 1 if you are safe. Press 2 if you need help. Press 3 to speak with someone.`, target: 'non_responders', voice_preset: 'friendly_female' },
    { wave: 3, channel: 'case_manager_task', delay_after_previous: '8h', message_template: '', target: 'still_unreachable' },
  ]);

  const [previewRequested, setPreviewRequested] = useState(false);

  // Audience preview — fires when filters are set OR user clicks Preview
  const hasFilters = (filters.zip_codes?.length || 0) > 0 ||
    (filters.states?.length || 0) > 0 ||
    (filters.risk_levels?.length || 0) > 0;

  const { data: preview, isLoading: previewLoading, refetch: refetchPreview } = useQuery({
    queryKey: ['audience-preview', filters],
    queryFn: () => emergencyAPI.previewAudience(filters),
    enabled: currentStep === 0 && (hasFilters || previewRequested),
  });

  // Launch mutation
  const launchMutation = useMutation({
    mutationFn: () => emergencyAPI.launchCampaign({
      incident_id: incidentId,
      audience_filters: filters,
      waves,
      bypass_quiet_hours: true,
    }),
    onSuccess: () => onComplete(),
  });

  const canProceed = () => {
    if (currentStep === 0) return (preview?.total_members || 0) > 0 || previewRequested;
    if (currentStep === 1) return waves.some(w => w.channel === 'case_manager_task' || w.message_template.length > 0);
    if (currentStep === 2) return waves.length > 0;
    return true;
  };

  return (
    <div className="bg-white min-h-screen">
      {/* Header */}
      <div className="border-b px-6 py-4">
        {!tabbed && (
        <h1 className="text-lg font-bold">Launch Emergency Campaign</h1>
        )}
        <p className="text-sm text-gray-500">Incident: {incidentName}</p>
      </div>

      {/* Step indicator */}
      <div className="px-6 py-3 border-b bg-gray-50">
        <div className="flex items-center gap-1">
          {STEPS.map((step, i) => (
            <React.Fragment key={step.id}>
              {i > 0 && <ChevronRight className="w-4 h-4 text-gray-300" />}
              <button
                onClick={() => i <= currentStep && setCurrentStep(i)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium ${
                  i === currentStep ? 'bg-blue-600 text-white' :
                  i < currentStep ? 'bg-blue-100 text-blue-700' :
                  'text-gray-400'
                }`}
              >
                <step.icon className="w-3.5 h-3.5" />
                {step.label}
              </button>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step content */}
      <div className="px-6 py-6 max-w-3xl">
        {currentStep === 0 && (
          <AudienceStep
            filters={filters}
            onChange={setFilters}
            preview={preview || null}
            isLoadingPreview={previewLoading}
            onPreviewAll={() => { setPreviewRequested(true); refetchPreview(); }}
          />
        )}

        {currentStep === 1 && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Configure the message for each wave. Use {"{"}<code>first_name</code>{"}"} for personalization.
            </p>
            <WaveConfigurator waves={waves} onChange={setWaves} />
          </div>
        )}

        {currentStep === 2 && (
          <WaveConfigurator waves={waves} onChange={setWaves} />
        )}

        {currentStep === 3 && (
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div>
                  <h3 className="text-sm font-semibold text-yellow-800">Review Before Launching</h3>
                  <p className="text-xs text-yellow-700 mt-1">
                    This will send emergency communications to <b>{preview?.total_members || '?'}</b> members
                    across <b>{waves.length}</b> escalation waves. This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>

            <div className="border rounded-lg divide-y">
              <div className="px-4 py-3">
                <div className="text-xs font-medium text-gray-500">Audience</div>
                <div className="text-sm font-semibold">{preview?.total_members || 0} members</div>
              </div>
              {waves.map((w, i) => (
                <div key={i} className="px-4 py-3">
                  <div className="text-xs font-medium text-gray-500">Wave {w.wave} — {w.channel}</div>
                  <div className="text-sm text-gray-700">
                    {w.delay_after_previous && `After ${w.delay_after_previous} delay → `}
                    {w.channel === 'case_manager_task' ? 'Create welfare check tasks' :
                     w.message_template ? w.message_template.substring(0, 80) + '...' : 'No message configured'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t px-6 py-3 flex items-center justify-between">
        <button
          onClick={currentStep === 0 ? onCancel : () => setCurrentStep(s => s - 1)}
          className="flex items-center gap-1 px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
        >
          <ChevronLeft className="w-4 h-4" />
          {currentStep === 0 ? 'Cancel' : 'Back'}
        </button>

        {currentStep < STEPS.length - 1 ? (
          <button
            onClick={() => setCurrentStep(s => s + 1)}
            disabled={!canProceed()}
            className="flex items-center gap-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={() => launchMutation.mutate()}
            disabled={launchMutation.isPending}
            className="flex items-center gap-1.5 px-5 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            <Rocket className="w-4 h-4" />
            {launchMutation.isPending ? 'Launching...' : 'Launch Campaign'}
          </button>
        )}
      </div>
    </div>
  );
};

export default EmergencyCampaignWizard;
